import { LegalLayout } from '@/components/LegalLayout';
import { useLanguage } from '@/lib/i18n/LanguageContext';

export default function TermsPage() {
  const { lang } = useLanguage();

  if (lang === 'tr') return (
    <LegalLayout title="Kullanım Şartları" lastUpdated="24 Şubat 2026">
      <p>Bu Kullanım Şartları, safecrypto.ai ("Platform") üzerinden sunulan hizmetlerin kullanımına ilişkin koşulları belirler. Platformu kullanarak bu şartları kabul etmiş sayılırsınız.</p>

      <h2>1. Hizmet Tanımı</h2>
      <p>Safe Crypto, kullanıcılarına aşağıdaki hizmetleri sunar:</p>
      <ul>
        <li>Kripto para alım-satım ve takas işlemleri</li>
        <li>Dijital cüzdan hizmetleri</li>
        <li>On-chain yatırma ve çekme işlemleri</li>
        <li>Kripto kart başvuru ve yönetimi</li>
        <li>Piyasa verileri ve analiz araçları</li>
      </ul>

      <h2>2. Hesap Oluşturma</h2>
      <ul>
        <li>Platforma kayıt olmak için 18 yaşından büyük olmanız gerekmektedir.</li>
        <li>Kayıt sırasında verdiğiniz bilgilerin doğru ve güncel olduğunu taahhüt edersiniz.</li>
        <li>Hesap güvenliğinizden (şifre, 2FA) siz sorumlusunuz.</li>
        <li>Bir kişi yalnızca bir hesap açabilir.</li>
      </ul>

      <h2>3. KYC/AML Yükümlülükleri</h2>
      <p>Yasal düzenlemelere uygun olarak:</p>
      <ul>
        <li>Belirli işlem limitleri üzerinde KYC doğrulaması zorunludur.</li>
        <li>Çekim işlemleri için kimlik doğrulamasının tamamlanması gereklidir.</li>
        <li>Şüpheli işlemler yetkili makamlara bildirilir.</li>
        <li>Platform, herhangi bir hesabı inceleme ve askıya alma hakkını saklı tutar.</li>
      </ul>

      <h2>4. İşlem Kuralları</h2>
      <ul>
        <li>Tüm işlemler geri alınamaz niteliktedir.</li>
        <li>Yanlış adrese yapılan transferlerden platform sorumlu değildir.</li>
        <li>İşlem ücretleri ve komisyon oranları platformda belirtilir.</li>
        <li>Platform, piyasa koşullarına göre oranları güncelleme hakkını saklı tutar.</li>
      </ul>

      <h2>5. Yasaklanan Faaliyetler</h2>
      <ul>
        <li>Kara para aklama veya terörün finansmanı</li>
        <li>Dolandırıcılık, sahtecilik veya kimlik hırsızlığı</li>
        <li>Platformun teknik altyapısına müdahale</li>
        <li>Otomatik bot veya scraper kullanımı</li>
        <li>Piyasa manipülasyonu</li>
      </ul>

      <h2>6. Risk Uyarısı</h2>
      <p><strong>Kripto para yatırımları yüksek risk içerir.</strong> Piyasa değerlerinde ani ve büyük dalgalanmalar yaşanabilir. Yatırım kararlarınızdan yalnızca siz sorumlusunuz. Platform yatırım tavsiyesi vermez.</p>

      <h2>7. Sorumluluk Sınırlaması</h2>
      <p>Platform, blockchain ağ gecikmelerinden, üçüncü taraf hizmet kesintilerinden, doğal afetlerden veya mücbir sebeplerden kaynaklanan zararlardan sorumlu tutulamaz.</p>

      <h2>8. Değişiklikler</h2>
      <p>Bu kullanım şartları önceden bildirim yapılarak güncellenebilir. Güncellemeler platformda yayınlandığı tarihte yürürlüğe girer.</p>

      <h2>9. Uygulanacak Hukuk</h2>
      <p>Bu şartlar Türkiye Cumhuriyeti hukuku kapsamında değerlendirilir. Uyuşmazlıklarda İstanbul mahkemeleri ve icra daireleri yetkilidir.</p>
    </LegalLayout>
  );

  return (
    <LegalLayout title="Terms of Service" lastUpdated="February 24, 2026">
      <p>These Terms of Service define the conditions for using services provided through safecrypto.ai ("Platform"). By using the Platform, you agree to these terms.</p>

      <h2>1. Service Description</h2>
      <ul>
        <li>Cryptocurrency buy/sell and swap transactions</li>
        <li>Digital wallet services</li>
        <li>On-chain deposit and withdrawal operations</li>
        <li>Crypto card application and management</li>
        <li>Market data and analysis tools</li>
      </ul>

      <h2>2. Account Creation</h2>
      <ul>
        <li>You must be at least 18 years old to register.</li>
        <li>You confirm that the information provided during registration is accurate and up-to-date.</li>
        <li>You are responsible for your account security (password, 2FA).</li>
        <li>Only one account per person is allowed.</li>
      </ul>

      <h2>3. KYC/AML Obligations</h2>
      <ul>
        <li>KYC verification is mandatory above certain transaction limits.</li>
        <li>Identity verification must be completed for withdrawal operations.</li>
        <li>Suspicious transactions are reported to the authorities.</li>
        <li>The platform reserves the right to review and suspend any account.</li>
      </ul>

      <h2>4. Transaction Rules</h2>
      <ul>
        <li>All transactions are irreversible.</li>
        <li>The platform is not responsible for transfers to incorrect addresses.</li>
        <li>Transaction fees and commission rates are displayed on the platform.</li>
      </ul>

      <h2>5. Prohibited Activities</h2>
      <ul>
        <li>Money laundering or financing of terrorism</li>
        <li>Fraud, forgery or identity theft</li>
        <li>Interfering with the platform's technical infrastructure</li>
        <li>Market manipulation</li>
      </ul>

      <h2>6. Risk Warning</h2>
      <p><strong>Cryptocurrency investments carry high risk.</strong> Market values can experience sudden and large fluctuations. You are solely responsible for your investment decisions. The platform does not provide investment advice.</p>

      <h2>7. Limitation of Liability</h2>
      <p>The platform cannot be held responsible for damages arising from blockchain network delays, third-party service interruptions, natural disasters or force majeure events.</p>

      <h2>8. Governing Law</h2>
      <p>These terms are governed by the laws of the Republic of Turkey. Istanbul courts and enforcement offices are authorized in disputes.</p>
    </LegalLayout>
  );
}
